import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { customMetrics, mibs, probes, type MibObject, type Probe, type ProbeAnswer } from "../lib/api.js";
import {
  disagreement,
  labelCandidates,
  metricFromObject,
  suggestLabel,
  type LabelChoice,
} from "../lib/mib-picker.js";

/**
 * Adding a custom metric by name from the organization's MIB library.
 *
 * Search, choose an object, and see what this device actually answers for it
 * before adding: vendors' MIBs are often wrong about their own agents, and a
 * live value is the only thing that shows that before a graph is built on it.
 */

/** How long to wait for the poller to answer. Past any SNMP timeout and retry. */
const PROBE_TIMEOUT_MS = 30_000;
const PROBE_POLL_MS = 700;
/** Label columns probed alongside the object; more is a table dump. */
const MAX_LABEL_PROBES = 6;
/** Rows previewed. The poller sends up to 32. */
const PREVIEW_ROWS = 8;

async function runProbe(deviceId: string, requests: { oid: string; column: boolean }[]): Promise<Probe> {
  const { id } = await probes.start(deviceId, requests);
  const deadline = Date.now() + PROBE_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, PROBE_POLL_MS));
    const probe = await probes.get(id);
    if (probe.status === "done" || probe.status === "error") return probe;
  }
  return { status: "error", result: null, error: "The poller did not answer in 30 seconds" };
}

export function MibPicker({ deviceId, onDone }: { deviceId: string; onDone: () => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<MibObject[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<MibObject | null>(null);

  useEffect(() => {
    if (chosen) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      mibs
        .search(query)
        .then((r) => !cancelled && (setResults(r.objects), setSearchError(null)))
        .catch((e: unknown) => !cancelled && setSearchError(e instanceof Error ? e.message : "Search failed"));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, chosen]);

  if (chosen) {
    return <ChosenObject deviceId={deviceId} object={chosen} onBack={() => setChosen(null)} onDone={onDone} />;
  }

  return (
    <div style={panel}>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="search the MIB library: cpu, temperature, power…"
          style={{ ...input, flex: 1 }}
        />
        <button type="button" onClick={onDone} style={button}>[ CANCEL ]</button>
      </div>
      {searchError && <p style={errorText}>{`!! ${searchError}`}</p>}
      {results && results.length === 0 && (
        <p style={help}>
          {query === "" ? (
            <>The library is empty. <Link to="/mibs" style={{ color: "inherit" }}>Upload the manufacturer's MIBs</Link> first.</>
          ) : (
            <>Nothing graphable matches. Is the vendor's MIB in the <Link to="/mibs" style={{ color: "inherit" }}>library</Link>?</>
          )}
        </p>
      )}
      {results && results.length > 0 && (
        <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid var(--gridline)" }}>
          {results.map((o) => (
            <button key={`${o.module_name}::${o.name}`} type="button" onClick={() => setChosen(o)} style={resultRow}>
              <span style={{ color: "var(--text-primary)" }}>{o.name}</span>
              <span style={{ color: "var(--text-muted)" }}>
                {" "}
                {o.module_name} · {o.kind === "counter" ? `counter${o.counter_bits ? ` (${o.counter_bits}-bit)` : ""}` : "gauge"}
                {o.is_column ? ` · column of ${o.table_name}` : " · one value"}
                {o.units ? ` · ${o.units}` : ""}
              </span>
              {o.description && <span style={descriptionLine}>{o.description}</span>}
            </button>
          ))}
          {results.length === 200 && <p style={{ ...help, padding: "6px 10px" }}>First 200 shown; narrow the search.</p>}
        </div>
      )}
    </div>
  );
}

function ChosenObject({
  deviceId,
  object,
  onBack,
  onDone,
}: {
  deviceId: string;
  object: MibObject;
  onBack: () => void;
  onDone: () => void;
}) {
  const [choices, setChoices] = useState<LabelChoice[]>([]);
  const [label, setLabel] = useState<LabelChoice | null>(null);
  const [probe, setProbe] = useState<Probe | null>(null);
  const [probeError, setProbeError] = useState<string | null>(null);
  const [form, setForm] = useState(() => metricFromObject(object, null));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Find the table's label columns, then read the object and all of them in
  // one probe, so switching the label afterwards needs no second wait.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const siblings = object.is_column && object.table_name ? (await mibs.table(object.module_name, object.table_name)).objects : [];
      const found = labelCandidates(object, siblings).slice(0, MAX_LABEL_PROBES);
      if (cancelled) return;
      setChoices(found);
      setLabel(suggestLabel(found));
      const result = await runProbe(deviceId, [
        { oid: object.oid, column: object.is_column },
        ...found.map((c) => ({ oid: c.oid, column: true })),
      ]);
      if (!cancelled) setProbe(result);
    })().catch((e: unknown) => !cancelled && setProbeError(e instanceof Error ? e.message : "Could not read the device"));
    return () => {
      cancelled = true;
    };
  }, [deviceId, object]);

  const answers = new Map<string, ProbeAnswer>((probe?.result ?? []).map((a) => [a.oid, a]));
  const main = answers.get(object.oid);
  const labels = new Map((label ? answers.get(label.oid)?.rows ?? [] : []).map((r) => [r.index, r.text.replace(/^"|"$/g, "")]));
  const warning = main ? disagreement(form.kind, main.rows) : null;

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await customMetrics.create(deviceId, { ...form, label_oid: object.is_column ? label?.oid ?? null : null });
      onDone();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : "Could not add");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={panel}>
      <div style={{ fontSize: 13 }}>
        {object.name} <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{object.module_name} · {object.oid}</span>
      </div>
      {object.description && <p style={{ ...help, margin: 0, whiteSpace: "pre-line" }}>{object.description.trim()}</p>}

      <div style={subLabel}>ON THIS DEVICE NOW</div>
      {!probe && !probeError && <p style={help}>{">> Reading the device…"}</p>}
      {probeError && <p style={errorText}>{`!! ${probeError}`}</p>}
      {probe?.status === "error" && <p style={errorText}>{`!! ${probe.error ?? "The read failed"}`}</p>}
      {main?.problem && <p style={errorText}>{`!! ${main.problem}`}</p>}
      {main && main.rows.length > 0 && (
        <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
          <tbody>
            {main.rows.slice(0, PREVIEW_ROWS).map((r) => (
              <tr key={r.index}>
                {object.is_column && <td style={cell}>{labels.get(r.index) ?? `row ${r.index}`}</td>}
                <td style={{ ...cell, color: "var(--text-primary)" }}>{r.text}</td>
                <td style={{ ...cell, color: "var(--text-muted)" }}>{r.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {main && main.total > PREVIEW_ROWS && <p style={{ ...help, margin: 0 }}>{`…and ${main.total - PREVIEW_ROWS} more rows.`}</p>}
      {warning && <p style={{ ...errorText, color: "var(--status-warning)" }}>{`!! ${warning}`}</p>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label style={field}>
          NAME
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={input} />
        </label>
        <label style={field}>
          TYPE
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as "gauge" | "counter" })} style={input}>
            <option value="gauge">GAUGE — PLOT AS READ</option>
            <option value="counter">COUNTER — PLOT AS A RATE</option>
          </select>
        </label>
        {object.is_column && (
          <label style={field}>
            ROW LABEL
            <select
              value={label?.oid ?? ""}
              onChange={(e) => setLabel(choices.find((c) => c.oid === e.target.value) ?? null)}
              style={input}
            >
              <option value="">ROW NUMBER</option>
              {choices.map((c) => <option key={c.oid} value={c.oid}>{c.name}</option>)}
            </select>
          </label>
        )}
        <label style={{ ...field, maxWidth: 90 }}>
          SCALE
          <input type="number" step="any" value={form.scale ?? 1} onChange={(e) => setForm({ ...form, scale: Number(e.target.value) })} style={input} />
        </label>
        <label style={{ ...field, maxWidth: 110 }}>
          UNIT
          <input value={form.unit ?? ""} onChange={(e) => setForm({ ...form, unit: e.target.value || null })} style={input} />
        </label>
      </div>
      <p style={{ ...help, margin: 0 }}>
        Scale is never taken from the MIB: leave it at 1 unless the description says the device reports in tenths,
        say. The unit above is the MIB's own wording; shorten it if you like.
      </p>
      {saveError && <p style={errorText}>{`!! ${saveError}`}</p>}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" disabled={saving || form.name.trim() === ""} onClick={() => void save()} style={button}>[ ADD ]</button>
        <button type="button" onClick={onBack} style={button}>[ BACK ]</button>
        <button type="button" onClick={onDone} style={button}>[ CANCEL ]</button>
      </div>
    </div>
  );
}

const panel: React.CSSProperties = { border: "1px solid var(--border)", padding: 12, margin: "12px 0", display: "grid", gap: 8 };
const help: React.CSSProperties = { color: "var(--text-muted)", fontSize: 11, lineHeight: 1.5 };
const errorText: React.CSSProperties = { color: "var(--status-critical)", fontSize: 12, margin: 0 };
const subLabel: React.CSSProperties = { fontSize: 11, color: "var(--text-secondary)", letterSpacing: "0.1em", marginTop: 4 };
const field: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 3, fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.08em", flex: 1, minWidth: 140 };
const input: React.CSSProperties = { padding: "6px 8px", fontSize: 12, border: "1px solid var(--border)", background: "var(--page-plane)", color: "var(--text-primary)", fontFamily: "inherit" };
const button: React.CSSProperties = { background: "none", border: "1px solid var(--border)", padding: "5px 9px", fontSize: 11, color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.08em", whiteSpace: "nowrap" };
const cell: React.CSSProperties = { padding: "2px 14px 2px 0", color: "var(--text-secondary)" };
const resultRow: React.CSSProperties = {
  display: "block", width: "100%", textAlign: "left", background: "none", border: "none",
  borderBottom: "1px solid var(--gridline)", padding: "6px 10px", cursor: "pointer", fontFamily: "inherit", fontSize: 12,
};
const descriptionLine: React.CSSProperties = {
  display: "block", color: "var(--text-muted)", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};
